import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { useLazyRecordingS3IntakeUploadCredentialsQuery } from "../../../api/entities/recordingS3IntakeIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { CopyValueButton } from "./copyableValue.js";

const MASKED_KEY_ID = "••••••••••••••••••••";
const MASKED_SECRET = "••••••••••••••••••••••••••••••••••••••••";

interface CredentialValueProps {
  title: string;
  value: string | null;
  masked: string;
  label: string;
}

const CredentialValue: FC<CredentialValueProps> = (props) => {
  const { title, value, masked, label } = props;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 rounded-md border border-border-light bg-muted px-2.5 py-1.5 font-mono text-xs break-all text-foreground">
          {value ?? masked}
        </code>
        {value !== null ? (
          <CopyValueButton value={value} label={label} />
        ) : null}
      </div>
    </div>
  );
};

/**
 * The S3 uploader credentials (access key id and secret access key), revealed
 * on demand. Both are fetched only when the teammate asks, through the
 * UPDATE-gated reveal endpoint, which reads the bucket's current key from
 * Alpharun's secret store; so after a key rotation the next reveal shows the
 * new key. Hiding them (or leaving the page) calls the hook's `reset()`, which
 * drops the result from the RTK Query cache as well as from this component, so
 * nothing retains the secret once it is off screen. Read-only: the credentials
 * are provisioned by Alpharun with the bucket and cannot be changed here.
 */
export const RecordingS3IntakeUploadCredentials: FC = () => {
  const [reveal, { data, isFetching, error, reset }] =
    useLazyRecordingS3IntakeUploadCredentialsQuery();
  const [isVisible, setIsVisible] = useState(false);
  const credentials = isVisible ? (data ?? null) : null;

  useEffect(() => reset, [reset]);

  const handleToggle = async () => {
    if (isVisible) {
      setIsVisible(false);
      reset();
      return;
    }
    try {
      // Always refetch: the value is never reused from the cache.
      await reveal(undefined, false).unwrap();
      setIsVisible(true);
    } catch {
      // `error` from the hook renders the failure below.
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-foreground">Upload credentials</p>
      <p className="text-sm text-muted-foreground">
        The AWS access key ID and secret access key your call center uses to
        upload recordings. Hidden until you reveal them; each reveal is logged.
      </p>
      <div className="space-y-2">
        <CredentialValue
          title="Access key ID"
          value={credentials?.aws_access_key_id ?? null}
          masked={MASKED_KEY_ID}
          label="access key ID"
        />
        <CredentialValue
          title="Secret access key"
          value={credentials?.aws_secret_access_key ?? null}
          masked={MASKED_SECRET}
          label="secret access key"
        />
      </div>
      <Can
        I={PermissionActions.UPDATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) => (
          <Button
            size={Size.SMALL}
            variant={ButtonVariant.Secondary}
            isDisabled={!allowed}
            isLoading={isFetching}
            onClick={() => void handleToggle()}
            icon={
              credentials !== null ? (
                <IconEyeOff className="size-4" />
              ) : (
                <IconEye className="size-4" />
              )
            }
          >
            {credentials !== null ? "Hide" : "Reveal"}
          </Button>
        )}
      </Can>
      {error ? (
        <p className="text-sm text-red-600">
          {getApiErrorMessage(error, "Couldn’t reveal the upload credentials.")}
        </p>
      ) : null}
    </div>
  );
};
